/**
 * /market/table/:token — Landing del QR de mesa Dine-In.
 * Resuelve la mesa, abre sesión si no hay una, y redirige a la experiencia
 * mobile-first `/market/:slug/mesa` (no al storefront global).
 */
import { createFileRoute, redirect } from "@tanstack/react-router";
import {
  dbSmGetTableByQrToken,
  dbSmGetOpenTableSession,
  dbSmOpenTableSession,
} from "@/lib/db-service/tables";
import { sql } from "@/lib/db";
import { observeBusiness } from "@/lib/observability/ingest";
import { MarketPageSkeleton } from "@/components/market/MarketSkeletons";

export const Route = createFileRoute("/market/table/$token")({
  pendingComponent: MarketPageSkeleton,
  pendingMs: 0,
  async loader({ params }) {
    const table = await dbSmGetTableByQrToken(params.token);
    if (!table) throw new Error("Mesa no encontrada o QR inválido");

    const db = sql();
    const [tenantRow] = await db`SELECT slug FROM sm_tenants WHERE id = ${table.tenantId} LIMIT 1`;
    const tenantSlug = tenantRow?.slug ? String(tenantRow.slug) : null;
    if (!tenantSlug) throw new Error("QR inválido — tenant sin slug");

    let session = await dbSmGetOpenTableSession(table.tenantId, table.id);
    if (!session) {
      session = await dbSmOpenTableSession(table.tenantId, {
        tableId: table.id,
        openedBy: "qr-guest",
        comensales: 1,
        notes: "Abierta por QR del cliente",
      });
    }

    observeBusiness(
      "qr_mesa_scanned",
      { tenantId: table.tenantId },
      {
        tableId: table.id,
        tableName: table.nombre,
        sessionId: session.id,
        qrToken: params.token,
        channel: "qr_mesa",
      },
      { type: "sm_tables", id: table.id },
    );

    throw redirect({
      to: "/market/$slug/mesa",
      params: { slug: tenantSlug },
      search: {
        table: params.token,
        session: session.id,
        mesa: table.nombre,
        utm_source: "qr_mesa",
        utm_medium: "dine_in",
      },
      replace: true,
    });
  },
  component: TableQrLanding,
  errorComponent: ({ error }) => (
    <div className="flex min-h-dvh flex-col items-center justify-center gap-4 bg-[#0a1620] p-8 text-center text-white">
      <p className="text-lg font-semibold">QR no válido</p>
      <p className="text-sm text-white/60">
        {String((error as Error)?.message ?? "Este código QR no corresponde a ninguna mesa activa.")}
      </p>
    </div>
  ),
});

function TableQrLanding() {
  return (
    <div className="flex min-h-dvh items-center justify-center bg-[#0a1620]">
      <p className="animate-pulse text-sm text-white/60">Abriendo menú de la mesa…</p>
    </div>
  );
}
